// src/pages/community/HallofFamePage.jsx
import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Crown, MessageCircle, ChevronDown, Loader2, Users } from 'lucide-react';
import { fetchCompetitions, fetchSubmissions } from '@/services/apiService';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';

/**
 * ============================================================================
 * Hall of Fame Page
 * ============================================================================
 *
 * Sections:
 * 1. Leaderboard    — per-competition, approved submissions sorted by votes
 * 2. Recent Winners — hardcoded by Joe, update WINNERS array below
 * 3. WhatsApp CTA   — 
 *
 * ─── TO ADD/UPDATE WINNERS ───────────────────────────────────────────────
 * Edit the WINNERS array below. Each winner needs:
 *   - competition: name of the competition
 *   - username: winner's username
 *   - avatar: their avatar URL (from profile) or null for initials
 *   - prize: what they won
 *   - date: when they won (display string)
 *   - submissionImg: thumbnail of winning submission (optional)
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ─── WHATSAPP LINK ───────────────────────────────────────────────────────
 * Per-partner WhatsApp links are configured via the Tenant branding JSON
 * in Django admin. Set branding.whatsapp_url for the partner.
 * Falls back to the default Zelospace link if not set.
 * ─────────────────────────────────────────────────────────────────────────
 */

// ─── UPDATE THIS ARRAY TO ADD WINNERS ────────────────────────────────────────
const WINNERS = [
  // Example — replace with real winners:
  // {
  //   competition: "Nike x Zelospace Dance Challenge",
  //   username: "mirtha",
  //   avatar: null,
  //   prize: "£500 Nike voucher",
  //   date: "February 2026",
  //   submissionImg: "https://storage.googleapis.com/..."
  // },
];
// ─────────────────────────────────────────────────────────────────────────────

// Medal styles for top 3
const RANK_CONFIG = {
  1: { icon: Crown,  color: 'text-mikado-yellow', bg: 'bg-mikado-yellow/10', label: '1st' },
  2: { icon: Medal,  color: 'text-zinc-300',       bg: 'bg-zinc-300/10',      label: '2nd' },
  3: { icon: Trophy, color: 'text-amber-600',      bg: 'bg-amber-600/10',     label: '3rd' },
};

// Generate initials avatar
const Initials = ({ name, size = 'md' }) => {
  const initials = (name || '?')
    .split(' ')
    .map(x => x[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const sizeClass = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';

  return (
    <div className={`${sizeClass} rounded-full bg-stone-brown border border-pebble-brown/30 flex items-center justify-center font-bold text-swan flex-shrink-0`}>
      {initials}
    </div>
  );
};

const HallofFamePage = () => {
  // ✅ useTenant called inside component — correct
  const { branding } = useTenant();
  const WHATSAPP_URL = branding?.whatsapp_url || 'https://chat.whatsapp.com/JrbcQY7cngmEWnyc3AlArU';

  const [competitions, setCompetitions] = useState([]);
  const [selectedComp, setSelectedComp] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingComps, setLoadingComps] = useState(true);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch competitions on mount
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCompetitions();
        const list = Array.isArray(data) ? data : data?.results || [];
        setCompetitions(list);
        if (list.length > 0) setSelectedComp(list[0]);
      } catch (e) {
        console.error('[HallofFamePage] Failed to fetch competitions:', e);
      } finally {
        setLoadingComps(false);
      }
    };
    load();
  }, []);

  // Fetch leaderboard when competition changes
  useEffect(() => {
    if (!selectedComp) return;

    const load = async () => {
      setLoadingLeaderboard(true);
      try {
        const data = await fetchSubmissions({ comp_id: selectedComp.id, approved: true });
        const list = Array.isArray(data) ? data : data?.results || [];

        // Sort by votes_count descending
        const sorted = [...list].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
        setLeaderboard(sorted);
      } catch (e) {
        console.error('[HallofFamePage] Failed to fetch leaderboard:', e);
        setLeaderboard([]);
      } finally {
        setLoadingLeaderboard(false);
      }
    };

    load();
  }, [selectedComp]);

  return (
    <div className="min-h-screen w-full bg-stone-brown px-4 py-20">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* ── Page Header ── */}
        <div>
          <h1 className="text-4xl font-bold text-white font-poppins mb-2">
            Hall of Fame
          </h1>
          <p className="text-swan/60 text-sm">
            See who's leading and celebrate our winners! 🚀
          </p>
        </div>

        {/* ── Section 1: Leaderboard ── */}
        <div className="bg-raisin-black rounded-xl p-6">

          {/* Section Header + Competition Selector */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Trophy className="text-mikado-yellow" size={20} />
              <h2 className="text-xl font-bold text-white font-poppins">
                Leaderboard
              </h2>
            </div>

            {/* Competition Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 bg-stone-brown/40 border border-pebble-brown/30 rounded-lg px-4 py-2 text-sm text-swan hover:border-mikado-yellow/30 transition-all"
              >
                {loadingComps ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <span className="max-w-[200px] truncate">
                      {selectedComp?.title || 'Select competition'}
                    </span>
                    <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </>
                )}
              </button>

              {dropdownOpen && competitions.length > 0 && (
                <div className="absolute right-0 mt-2 w-64 bg-raisin-black border border-pebble-brown/30 rounded-lg shadow-xl z-20 overflow-hidden">
                  {competitions.map(comp => (
                    <button
                      key={comp.id}
                      onClick={() => {
                        setSelectedComp(comp);
                        setDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-all hover:bg-stone-brown/40 ${
                        selectedComp?.id === comp.id
                          ? 'text-mikado-yellow bg-stone-brown/30'
                          : 'text-swan'
                      }`}
                    >
                      {comp.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard List */}
          {loadingLeaderboard ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-mikado-yellow" size={24} />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Trophy size={40} className="text-swan/20 mx-auto mb-3" />
              <p className="text-swan/40 text-sm">No approved submissions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((submission, index) => {
                const rank = index + 1;
                const config = RANK_CONFIG[rank];
                const username = submission.user?.profile?.name || submission.user?.username || 'Unknown';
                const avatar = submission.user?.profile?.avatar_url;

                return (
                  <div
                    key={submission.id}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                      rank <= 3
                        ? `${config.bg} border-transparent`
                        : 'bg-stone-brown/20 border-pebble-brown/10'
                    }`}
                  >
                    {/* Rank */}
                    <div className="w-8 flex-shrink-0 text-center">
                      {rank <= 3 ? (
                        React.createElement(config.icon, {
                          size: 20,
                          className: config.color
                        })
                      ) : (
                        <span className="text-swan/40 text-sm font-bold">
                          {rank}
                        </span>
                      )}
                    </div>

                    {/* Avatar */}
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={username}
                        className="w-10 h-10 rounded-full object-cover border border-pebble-brown/30 flex-shrink-0"
                      />
                    ) : (
                      <Initials name={username} />
                    )}

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${rank <= 3 ? config.color : 'text-swan'}`}>
                        {username}
                      </p>
                      {rank <= 3 && (
                        <p className="text-xs text-swan/50">{config.label} place</p>
                      )}
                    </div>

                    {/* Votes */}
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold text-lg ${rank <= 3 ? config.color : 'text-swan/70'}`}>
                        {submission.votes_count || 0}
                      </p>
                      <p className="text-xs text-swan/40">votes</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Section 2: Recent Winners ── */}
        <div className="bg-raisin-black rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Crown className="text-mikado-yellow" size={20} />
            <h2 className="text-xl font-bold text-white font-poppins">
              Recent Winners
            </h2>
          </div>

          {WINNERS.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-pebble-brown/20 rounded-lg">
              <Crown size={36} className="text-swan/20 mx-auto mb-3" />
              <p className="text-swan/40 text-sm">Winners coming soon!</p>
              <p className="text-swan/30 text-xs mt-1">
                First competition ends soon — stay tuned
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WINNERS.map((winner, i) => (
                <div
                  key={i}
                  className="bg-stone-brown/30 rounded-lg p-4 border border-pebble-brown/20 flex gap-4"
                >
                  {winner.submissionImg && (
                    <img
                      src={winner.submissionImg}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-mikado-yellow font-semibold uppercase tracking-wide">
                      {winner.competition}
                    </span>
                    <div className="flex items-center gap-2 mt-1">
                      {winner.avatar ? (
                        <img
                          src={winner.avatar}
                          alt={winner.username}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <Initials name={winner.username} size="sm" />
                      )}
                      <p className="text-white font-bold truncate">
                        @{winner.username}
                      </p>
                    </div>
                    <p className="text-swan/60 text-xs mt-1">{winner.prize}</p>
                    <p className="text-swan/40 text-xs">{winner.date}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Section 3: WhatsApp CTA ── */}
        <div className="bg-raisin-black rounded-xl p-6 border border-green-500/20">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
                <MessageCircle className="text-green-400" size={32} />
              </div>
            </div>

            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-xl font-bold text-white font-poppins mb-1">
              Never Miss a Moment
              </h2>
        
              <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start">
                <Users size={14} className="text-green-400" />
                <span className="text-green-400 text-xs font-medium">
                Results, announcements and more 
                </span>
              </div>
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
            >
              <button className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all">
                <MessageCircle size={18} />
                Join on WhatsApp
              </button>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HallofFamePage;